import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const emptyForm = { name: "", code: "", contactInfo: "" };

const Brands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const loadBrands = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/brands", { size: 200 });
      setBrands(data?.content || []);
    } catch (error) {
      toast.error(error.message || "Failed to load brands");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBrands();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (brand) => {
    setEditingId(brand.id);
    setFormData({ name: brand.name || "", code: brand.code || "", contactInfo: brand.contactInfo || "" });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/api/v1/brands/${editingId}`, formData);
        toast.success("Brand updated");
      } else {
        await api.post("/api/v1/brands", formData);
        toast.success("Brand created");
      }
      setShowModal(false);
      loadBrands();
    } catch (error) {
      toast.error(error.message || "Failed to save brand");
    } finally {
      setSaving(false);
    }
  };

  const filteredBrands = brands.filter((brand) =>
    [brand.name, brand.code, brand.contactInfo].some((value) =>
      String(value || "").toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading brands...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search brand name / code"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="primary" onClick={openCreate}>
          + New Brand
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Code</th>
              <th>Contact Info</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBrands.length === 0 ? (
              <tr>
                <td colSpan={5}>No brands found.</td>
              </tr>
            ) : (
              filteredBrands.map((brand) => (
                <tr key={brand.id}>
                  <td>{brand.id}</td>
                  <td>{brand.name}</td>
                  <td>{brand.code}</td>
                  <td>{brand.contactInfo || "-"}</td>
                  <td className="action-buttons">
                    <button className="secondary" onClick={() => openEdit(brand)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingId ? "Edit Brand" : "Create Brand"}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name</label>
                  <input name="name" value={formData.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Code</label>
                  <input name="code" value={formData.code} onChange={handleChange} required />
                </div>
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label>Contact Info</label>
                  <input name="contactInfo" value={formData.contactInfo} onChange={handleChange} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Brands;
